import { useState } from "react";
import { ChevronDown, GripVertical, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  SubtaskConfigFields,
  type SubtaskMemberOption,
} from "./SubtaskConfigFields";
import { SubtaskSummaryChips } from "./SubtaskSummaryChips";

export interface DraftSubtask {
  tempId: string;
  title: string;
  description: string;
  assignee_member_ids: string[];
  due_date: string | null;
  due_time: string | null;
}

interface DraftSubtaskRowProps {
  draft: DraftSubtask;
  onChange: (patch: Partial<DraftSubtask>) => void;
  onRemove: () => void;
  isAdmin: boolean;
  members: SubtaskMemberOption[];
  memberName: (id: string) => string;
  minDate?: string;
  defaultOpen?: boolean;
  /** Mensagem de erro de validação da data (cronologia ou pai). */
  dateError?: string | null;
}

export function DraftSubtaskRow({
  draft,
  onChange,
  onRemove,
  isAdmin,
  members,
  memberName,
  minDate,
  defaultOpen = false,
  dateError,
}: DraftSubtaskRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const assigneeNames = draft.assignee_member_ids.map((id) => memberName(id));

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border bg-card"
    >
      <div className="flex items-center gap-2 px-2 py-1.5">
        <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40" />
        <Checkbox checked={false} disabled className="shrink-0" />
        <Input
          value={draft.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Título da subtarefa"
          className="h-9 flex-1 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0"
        />
        {!open && (
          <SubtaskSummaryChips
            dueDate={draft.due_date}
            dueTime={draft.due_time}
            assigneeNames={assigneeNames}
            showAssignee={isAdmin}
            description={draft.description}
          />
        )}
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground"
            aria-label={open ? "Recolher subtarefa" : "Expandir subtarefa"}
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
            />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="space-y-3 border-t px-3 py-3">
          <SubtaskConfigFields
            idPrefix={`draft-${draft.tempId}`}
            description={draft.description}
            onDescriptionChange={(v) => onChange({ description: v })}
            assigneeIds={draft.assignee_member_ids}
            onAssigneeIdsChange={(ids) => onChange({ assignee_member_ids: ids })}
            date={draft.due_date ?? ""}
            onDateChange={(v) =>
              onChange({ due_date: v || null, due_time: v ? draft.due_time : null })
            }
            time={draft.due_time ?? ""}
            onTimeChange={(v) => onChange({ due_time: v || null })}
            isAdmin={isAdmin}
            members={members}
            minDate={minDate}
            dateError={dateError}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-muted-foreground hover:text-destructive"
              onClick={onRemove}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Remover
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
